import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lightning, MagnifyingGlass, CheckCircle, Clock, Trash, Image, UploadSimple, Phone, CreditCard, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DropInAdmin() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadingZelle, setUploadingZelle] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await axios.post(`${API}/admin/drop-in/reconcile`, {}, { withCredentials: true });
      toast.success(`Verificados ${data.checked} · ${data.fixed} confirmados como pagos`);
      fetchData();
    } catch { toast.error('Erro ao reconciliar'); } finally { setReconciling(false); }
  };

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/drop-in/registrations`, { withCredentials: true });
      setRegistrations(data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este registro?')) return;
    try {
      await axios.delete(`${API}/drop-in/registrations/${id}`, { withCredentials: true });
      toast.success('Registro removido');
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const handleStatusUpdate = async (id, status, paymentStatus) => {
    try {
      await axios.put(`${API}/drop-in/registrations/${id}`, { status, payment_status: paymentStatus }, { withCredentials: true });
      toast.success('Status atualizado');
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleZelleUpload = async (regId, file) => {
    if (!file) return;
    setUploadingZelle(regId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/drop-in/zelle-receipt?registration_id=${regId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Comprovante enviado! Pagamento confirmado.');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploadingZelle(null);
    }
  };

  const viewReceipt = (regId) => {
    const token = localStorage.getItem('access_token');
    window.open(`${API}/drop-in/zelle-receipt/${regId}?auth=${token}`, '_blank');
  };

  const [sortMode, setSortMode] = useState('recent');
  const filtered = registrations.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.phone?.includes(search) ||
    r.preferred_date?.includes(search)
  );
  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === 'name-asc') return (a.name || '').localeCompare(b.name || '', 'pt-BR');
    if (sortMode === 'name-desc') return (b.name || '').localeCompare(a.name || '', 'pt-BR');
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  const totalRegs = registrations.length;
  const paidCount = registrations.filter(r => r.payment_status === 'pago').length;
  const pendingCount = registrations.filter(r => r.payment_status === 'pendente').length;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="drop-in-admin-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Aulas Avulsas</h1>
            <p className="text-[#73706A] mt-1">Registros de aulas avulsas e pagamentos</p>
          </div>
          <button onClick={handleReconcile} disabled={reconciling} className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-black disabled:opacity-50 self-start" data-testid="reconcile-btn">
            <ArrowsClockwise size={16} weight="bold" /> {reconciling ? 'Verificando...' : 'Reconciliar Pagamentos'}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Total de Registros</p>
            <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{totalRegs}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Pagos</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">{paidCount}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Pendentes</p>
            <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{pendingCount}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
            <Input
              placeholder="Buscar por nome, telefone ou data..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-drop-in"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1" data-testid="dropin-sort-controls">
            <button onClick={() => setSortMode('recent')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'recent' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="dropin-sort-recent">Recentes</button>
            <button onClick={() => setSortMode('name-asc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="dropin-sort-asc">A-Z</button>
            <button onClick={() => setSortMode('name-desc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`} data-testid="dropin-sort-desc">Z-A</button>
          </div>
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-[#73706A]">
              {search ? 'Nenhum registro encontrado' : 'Nenhuma aula avulsa registrada'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F2EB]">
                    <th className="table-header">Nome</th>
                    <th className="table-header">Telefone</th>
                    <th className="table-header">Data</th>
                    <th className="table-header">Valor</th>
                    <th className="table-header">Pagamento</th>
                    <th className="table-header">Status</th>
                    <th className="table-header text-right">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((reg, index) => (
                    <tr key={reg.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`dropin-row-${index}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#E85D04] rounded-full flex items-center justify-center">
                            <Lightning size={18} className="text-white" />
                          </div>
                          <span className="font-medium text-[#1A1A1A]">{reg.name}</span>
                        </div>
                      </td>
                      <td className="table-cell text-[#1A1A1A]">{reg.phone}</td>
                      <td className="table-cell text-[#1A1A1A]">{reg.preferred_date}</td>
                      <td className="table-cell font-medium text-[#2D6A4F]">$ {(reg.amount || 25).toFixed(2)}</td>
                      <td className="table-cell">
                        <span className={`badge ${reg.payment_status === 'pago' ? 'badge-success' : 'badge-warning'}`}>
                          {reg.payment_status || 'pendente'}
                        </span>
                        {reg.payment_method && (
                          <span className="text-xs text-[#73706A] ml-1">({reg.payment_method})</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${reg.status === 'confirmada' ? 'badge-success' : 'badge-warning'}`}>
                          {reg.status || 'pendente'}
                        </span>
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {reg.payment_status !== 'pago' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStatusUpdate(reg.id, 'confirmada', 'pago')}
                                className="text-[#2D6A4F] hover:text-[#1B4332] hover:bg-green-50"
                                data-testid={`confirm-dropin-${index}`}
                                title="Confirmar pagamento manualmente"
                              >
                                <CheckCircle size={18} />
                              </Button>
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                                  className="hidden"
                                  onChange={(e) => handleZelleUpload(reg.id, e.target.files[0])}
                                  data-testid={`dropin-zelle-upload-${index}`}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50 pointer-events-none"
                                  disabled={uploadingZelle === reg.id}
                                  title="Enviar comprovante Zelle"
                                >
                                  {uploadingZelle === reg.id ? (
                                    <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <UploadSimple size={18} />
                                  )}
                                </Button>
                              </label>
                            </>
                          )}
                          {reg.zelle_receipt_path && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewReceipt(reg.id)}
                              className="text-[#0077B6] hover:text-[#03045E] hover:bg-blue-50"
                              data-testid={`view-dropin-receipt-${index}`}
                              title="Ver comprovante Zelle"
                            >
                              <Image size={18} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(reg.id)}
                            className="text-[#9B2226] hover:text-[#9B2226] hover:bg-red-50"
                            data-testid={`delete-dropin-${index}`}
                          >
                            <Trash size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Phone size={24} className="text-[#0077B6]" />
            <h3 className="font-['Outfit'] font-medium text-lg text-[#1A1A1A]">Pagamento Zelle</h3>
          </div>
          <p className="text-sm text-[#73706A]">
            Clique no icone <UploadSimple size={14} className="inline text-[#0077B6]" /> para enviar o comprovante Zelle do aluno. O sistema confirma o pagamento automaticamente.
          </p>
        </div>
      </div>
    </Layout>
  );
}
